/**
 * The unit an add-on (one extra covered unit) is priced in. Mirrors
 * Operations' membership-home-sale.ts, which is authoritative for what a
 * member is charged.
 */
export type MembershipExtraUnitPriceUnit = 'MONTH' | 'YEAR';

export interface MembershipExtraUnitPrice {
  extraUnitPrice: number;
  extraUnitPricePer: MembershipExtraUnitPriceUnit;
}

export const EXTRA_UNIT_PRICE_UNIT_LABELS: Record<MembershipExtraUnitPriceUnit, string> = {
  MONTH: 'per month',
  YEAR: 'per year',
};

export const EXTRA_UNIT_PRICE_UNIT_SUFFIX: Record<MembershipExtraUnitPriceUnit, string> = {
  MONTH: '/mo',
  YEAR: '/yr',
};

function money(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function finiteOrZero(value: unknown): number {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * The price a coverage rule (or a sold coverage selection) was entered in.
 * Rules saved before an add-on could be priced per year carry only
 * `extraUnitPriceMonthly`, which is a monthly price.
 */
export function extraUnitPriceOf(row: {
  extraUnitPrice?: unknown;
  extraUnitPricePer?: unknown;
  extraUnitPriceMonthly?: unknown;
}): MembershipExtraUnitPrice {
  if (row.extraUnitPrice !== undefined && row.extraUnitPrice !== null) {
    return {
      extraUnitPrice: Math.max(0, finiteOrZero(row.extraUnitPrice)),
      extraUnitPricePer: row.extraUnitPricePer === 'YEAR' ? 'YEAR' : 'MONTH',
    };
  }
  return {
    extraUnitPrice: Math.max(0, finiteOrZero(row.extraUnitPriceMonthly)),
    extraUnitPricePer: 'MONTH',
  };
}

/** Monthly equivalent of an add-on price, unrounded. */
export function extraUnitMonthlyEquivalent(price: MembershipExtraUnitPrice): number {
  return price.extraUnitPricePer === 'YEAR' ? price.extraUnitPrice / 12 : price.extraUnitPrice;
}

/**
 * What `extraUnits` extra units add to a billing period of `periodMonths`,
 * before the period total is rounded. A monthly add-on is charged in whole
 * cents per month; a yearly one is prorated from its yearly figure, so $59/yr
 * bills exactly $59.00 a year.
 */
export function extraUnitsChargeForPeriod(
  price: MembershipExtraUnitPrice,
  extraUnits: number,
  periodMonths: number,
): number {
  const units = Math.max(0, extraUnits);
  const unitPrice = Math.max(0, price.extraUnitPrice);
  return price.extraUnitPricePer === 'YEAR'
    ? (units * unitPrice * periodMonths) / 12
    : money(units * unitPrice) * periodMonths;
}
